import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { semanticGapService, type SemanticGap } from '../services/semanticGap.service';

type ListRecordsResponse = {
  records: SemanticGap[];
  total_records?: number;
  total_pages?: number;
};

export function useSemanticGaps(options?: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  enabled?: boolean;
}) {
  const { page = 1, pageSize = 100, filters = {}, enabled = true } = options || {};

  return useQuery<ListRecordsResponse>({
    queryKey: ['semanticGaps', page, pageSize, filters],
    queryFn: () => semanticGapService.find({ page, pageSize, filters }),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useSemanticGapById(id: string | null | undefined) {
  return useQuery<SemanticGap>({
    queryKey: ['semanticGap', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Gap ID is required'));
      return semanticGapService.findById(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useResolveSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useDismissSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useApproveSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useDeleteSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}
